/**
 * Candidate-only kinematic float support. This is not a buoyancy, immersion,
 * collision, shoreline-clearance, or hydrodynamic solver, and installs nothing.
 *
 * WebGL samples the actual indexed Float32 lake vertices, displaced with the
 * vertex expression in water-material.js, then interpolates their triangle.
 * It does NOT evaluate the sine at the query or use fragment-only ripple normals.
 * The normal is the upward geometric triangle normal, not the optical normal.
 * Clipped boundary cells can overlap in x/z: choose the uppermost displaced
 * triangle (a downward-ray support), then original index order for height ties.
 * JavaScript/GPU transcendental rounding is not guaranteed bit-identical.
 *
 * Geometry coordinates must already have world x/z: the current lake has only a
 * y translation. `meanY` is that translation (lake.position.y), not an additional
 * wave. Recreate after a geometry/attribute/index/draw-range replacement or edit.
 * No geometry, state, material, or uniform supplied by the caller is modified.
 */
export const MALLARD_WATER_SUPPORT_SCOPE = 'candidate-only-kinematic-float';

const DEFAULT_MEAN_Y = .18;
const EDGE_EPSILON = 1e-10;
const clamp01 = value => Math.max(0, Math.min(1, value));
const invalidGeometry = message => { throw new TypeError(`Mallard water geometry: ${message}`); };

function copyAttribute(source, size, name, expectedCount, fallback) {
  if (source == null) {
    if (fallback === undefined) invalidGeometry(`${name} is required`);
    return new Float32Array(expectedCount).fill(fallback);
  }
  const attribute = source.array != null || typeof source.getX === 'function';
  if (attribute && source.itemSize !== size) invalidGeometry(`${name} requires itemSize ${size}`);
  if (source.normalized) invalidGeometry(`${name} must not be normalized`);
  const array = attribute ? source.array : source;
  const count = attribute ? source.count : array?.length / size;
  if (!Number.isSafeInteger(count) || count < 1 || (expectedCount !== undefined && count !== expectedCount)) {
    invalidGeometry(`${name} has an invalid vertex count`);
  }
  if (!attribute && !Array.isArray(array) && !ArrayBuffer.isView(array)) invalidGeometry(`${name} must be an array`);
  const result = new Float32Array(count * size);
  const getters = ['getX', 'getY', 'getZ'];
  for (let vertex = 0; vertex < count; vertex++) {
    for (let component = 0; component < size; component++) {
      const value = attribute && typeof source[getters[component]] === 'function'
        ? source[getters[component]](vertex) : array?.[vertex * size + component];
      if (!Number.isFinite(value) || !Number.isFinite(Math.fround(value))) invalidGeometry(`${name} must contain finite Float32 values`);
      result[vertex * size + component] = value;
    }
  }
  return result;
}

function snapshotGeometry(geometry) {
  if (geometry == null) return null;
  const attributes = geometry.attributes ?? {};
  const positions = copyAttribute(attributes.position ?? geometry.positions, 3, 'position');
  const count = positions.length / 3;
  // These are the ShaderMaterial.defaultAttributeValues, not fitted wetness.
  const depths = copyAttribute(attributes.waterDepth ?? geometry.waterDepth, 1, 'waterDepth', count, .30);
  const progress = copyAttribute(attributes.riverProgress ?? geometry.riverProgress, 1, 'riverProgress', count, -1);
  const source = geometry.index ?? geometry.indices;
  if (source != null && ((!Array.isArray(source) && !ArrayBuffer.isView(source) && source.array == null) ||
      (source.array != null && source.itemSize !== 1) || source.normalized)) invalidGeometry('index must be an unnormalized scalar array');
  const indices = source == null ? Array.from({length: count}, (_, i) => i) : Array.from(source.array ?? source);
  if (indices.length % 3 || indices.some(i => !Number.isSafeInteger(i) || i < 0 || i >= count)) {
    invalidGeometry('index must contain complete triangles of valid vertex indices');
  }
  const start = geometry.drawRange?.start ?? 0;
  const drawCount = geometry.drawRange?.count ?? Infinity;
  if (!Number.isSafeInteger(start) || start < 0 || start % 3 ||
      !(drawCount === Infinity || (Number.isSafeInteger(drawCount) && drawCount >= 0 && drawCount % 3 === 0))) {
    invalidGeometry('drawRange must align to complete triangles');
  }
  const end = Math.min(indices.length, start + drawCount);
  const triangles = [];
  for (let offset = start; offset < end; offset += 3) {
    const ids = indices.slice(offset, offset + 3);
    const vertices = ids.map(i => ({x: positions[i * 3], y: positions[i * 3 + 1], z: positions[i * 3 + 2]}));
    const [a, b, c] = vertices;
    const bx = b.x - a.x, bz = b.z - a.z, cx = c.x - a.x, cz = c.z - a.z;
    const denominator = bx * cz - bz * cx;
    // Ring-zero and clipped boundary collapses are not supporting triangles.
    if (denominator === 0) continue;
    triangles.push({index: offset / 3, ids, vertices, bx, bz, cx, cz, denominator,
      minX: Math.min(a.x, b.x, c.x), maxX: Math.max(a.x, b.x, c.x),
      minZ: Math.min(a.z, b.z, c.z), maxZ: Math.max(a.z, b.z, c.z)});
  }
  return {triangles, depths, progress};
}

function containingTriangles(mesh, x, z) {
  const hits = [];
  for (const triangle of mesh.triangles) {
    if (x < triangle.minX || x > triangle.maxX || z < triangle.minZ || z > triangle.maxZ) continue;
    const px = x - triangle.vertices[0].x, pz = z - triangle.vertices[0].z;
    const b = (px * triangle.cz - pz * triangle.cx) / triangle.denominator;
    const c = (triangle.bx * pz - triangle.bz * px) / triangle.denominator;
    const a = 1 - b - c;
    if (a < -EDGE_EPSILON || b < -EDGE_EPSILON || c < -EDGE_EPSILON) continue;
    const weights = [a, b, c].map(clamp01), sum = weights[0] + weights[1] + weights[2];
    hits.push({triangle, weights: weights.map(value => value / sum)});
  }
  return hits;
}

function displacedY(vertex, depth, progress, time, strength) {
  const t = clamp01(depth / .10), wet = t * t * (3 - 2 * t);
  const river = progress >= 0 ? 1 : 0; // GLSL step(0.0, riverProgress).
  return vertex.y + wet * (1 - river) * (.002 + strength * .0025) *
    (Math.sin(vertex.x * 7.3 + vertex.z * 3.8 - time * 1.2) +
      .45 * Math.sin(vertex.x * 12.1 - vertex.z * 8.0 + time * 1.1));
}

/**
 * Accepts a BufferGeometry or {positions, waterDepth?, riverProgress?, indices?}
 * using flat arrays. Omitted scalar attributes match water-material defaults.
 * The snapshot owns its Float32 copy; changed mesh data needs a new sampler.
 *
 * sample(x, z, {time=0, windX=0, windY=0, meanY=.18, renderer='webgl', water})
 *   time: waterUniforms.time.value (weather clock, not animal animation clock)
 *   windX/Y: waterUniforms.wind.value.x/y, already wind-strength scaled
 *   meanY: lake.position.y; alternatively water gives .16 + water * .06
 *
 * Canvas is a flat mean plane. Supplied geometry restricts its query footprint;
 * without geometry it reports footprintValidated:false, requiring an independent
 * lake/habitat check. `supported` means this surface sample exists, never that a
 * duck's body, appendages, wet area, bank, or environmental clearance is safe.
 * Unsupported samples retain a finite diagnostic height and up normal; callers
 * MUST check supported before using them. No extrapolation outside a mesh.
 */
export function createMallardWaterSampler(geometry) {
  const mesh = snapshotGeometry(geometry);
  return function sampleMallardWater(x, z, options = {}) {
    const input = options && typeof options === 'object' ? options : {};
    const renderer = input.renderer === undefined ? 'webgl' : input.renderer;
    const meanY = input.meanY === undefined
      ? (input.water === undefined ? DEFAULT_MEAN_Y : Number.isFinite(input.water) ? .16 + input.water * .06 : NaN)
      : input.meanY;
    const result = {supported: false, height: Number.isFinite(meanY) ? meanY : DEFAULT_MEAN_Y,
      normal: {x: 0, y: 1, z: 0}, triangle: null,
      renderer: typeof renderer === 'string' ? renderer : 'invalid',
      scope: MALLARD_WATER_SUPPORT_SCOPE,
      surface: renderer === 'canvas' ? 'canvas-flat-mean-plane' : 'webgl-vertex-triangles',
      footprintValidated: false, reason: null};
    const fail = reason => ({...result, reason});
    const time = input.time === undefined ? 0 : input.time;
    const windX = input.windX === undefined ? 0 : input.windX;
    const windY = input.windY === undefined ? 0 : input.windY;
    if (options === null || typeof options !== 'object' || Array.isArray(options) || !Number.isFinite(x) || !Number.isFinite(z) ||
        ![time, windX, windY].every(Number.isFinite) ||
        !Number.isFinite(meanY) || (input.water !== undefined && !Number.isFinite(input.water))) return fail('invalid-input');
    if (renderer !== 'webgl' && renderer !== 'canvas') return fail('unsupported-renderer');
    if (!mesh && renderer === 'webgl') return fail('missing-geometry');
    const hits = mesh ? containingTriangles(mesh, x, z) : [];
    if (mesh && !hits.length) return fail('outside-mesh');
    if (renderer === 'canvas') return {...result, supported: true, footprintValidated: hits.length > 0};
    const strength = Math.min(Math.hypot(windX, windY), 2);
    let selected = null;
    for (const hit of hits) {
      const {triangle, weights} = hit;
      const vertices = triangle.vertices.map((vertex, i) => ({...vertex,
        y: displacedY(vertex, mesh.depths[triangle.ids[i]], mesh.progress[triangle.ids[i]], time, strength)}));
      if (!vertices.every(vertex => Number.isFinite(vertex.y))) return fail('nonfinite-displacement');
      const localHeight = weights.reduce((sum, weight, i) => sum + weight * vertices[i].y, 0);
      // Indexed order wins ties within double arithmetic noise at a shared edge.
      if (!selected || localHeight > selected.localHeight + Number.EPSILON * Math.max(1, Math.abs(localHeight))) {
        selected = {triangle, weights, vertices, localHeight};
      }
    }
    const {triangle, weights, vertices, localHeight} = selected;
    const [a, b, c] = vertices;
    const bx = b.x - a.x, by = b.y - a.y, bz = b.z - a.z;
    const cx = c.x - a.x, cy = c.y - a.y, cz = c.z - a.z;
    const nx = by * cz - bz * cy, ny = bz * cx - bx * cz, nz = bx * cy - by * cx;
    const length = Math.hypot(nx, ny, nz), sign = ny < 0 ? -1 : 1;
    const height = meanY + localHeight;
    if (!Number.isFinite(height) || !Number.isFinite(length) || length === 0) return fail('nonfinite-displacement');
    return {...result, supported: true, height, footprintValidated: true,
      normal: {x: nx / length * sign || 0, y: ny / length * sign, z: nz / length * sign || 0},
      triangle: {index: triangle.index, indices: triangle.ids.slice(), barycentric: weights,
        selection: 'uppermost-displaced-triangle', hits: hits.length,
        vertices: vertices.map(vertex => ({...vertex, y: meanY + vertex.y}))}};
  };
}
